import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Globe, Zap, Mail, Phone } from 'lucide-react';

interface FooterProps {
  onLoginClick?: () => void;
  onGetStartedClick?: () => void;
  onSignupClick?: () => void;
}

const Footer: React.FC<FooterProps> = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-950/72 backdrop-blur-md border-t border-white/15 text-slate-200">
      {/* Main Footer */}
      <div className="section-container py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2.5 mb-5 no-underline">
              <div className="w-9 h-9 bg-fintech-secondary rounded-lg flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-base">K</span>
              </div>
              <span className="text-white font-bold text-xl tracking-tight">Kenluk Pay</span>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6 max-w-xs">
              Enterprise-grade procurement and vendor payment infrastructure for modern businesses operating globally.
            </p>

            {/* Trust badges */}
            <div className="flex flex-col gap-2.5">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <div className="w-6 h-6 rounded-md bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <Shield size={12} className="text-fintech-accent" />
                </div>
                <span>Bank-grade security & encryption</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <div className="w-6 h-6 rounded-md bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <Globe size={12} className="text-fintech-accent" />
                </div>
                <span>50+ countries supported</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <div className="w-6 h-6 rounded-md bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <Zap size={12} className="text-fintech-accent" />
                </div>
                <span>99.9% uptime SLA</span>
              </div>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-widest">Company</h4>
            <ul className="flex flex-col gap-2.5">
              {[
                { to: '/about', label: 'About Us' },
                { to: '/careers', label: 'Careers' },
                { to: '/press', label: 'Press' },
                { to: '/contact', label: 'Contact' },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-slate-400 hover:text-white transition-colors no-underline"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-widest">Support</h4>
            <ul className="flex flex-col gap-2.5">
              {[
                { to: '/faq', label: 'FAQ' },
                { to: '/help', label: 'Help Center' },
                { to: '/contact', label: 'Contact Support' },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-slate-400 hover:text-white transition-colors no-underline"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              {/* Contact info */}
              <li className="mt-2 pt-2 border-t border-slate-700">
                <a href="mailto:Info@kenluk.com" className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors no-underline">
                  <Mail size={11} />
                  Info@kenluk.com
                </a>
              </li>
              <li>
                <a href="tel:+2347088329998" className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors no-underline">
                  <Phone size={11} />
                  +234 708 832 9998
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-widest">Legal</h4>
            <ul className="flex flex-col gap-2.5">
              {[
                { to: '/terms', label: 'Terms of Service' },
                { to: '/privacy', label: 'Privacy Policy' },
                { to: '/cookies', label: 'Cookie Policy' },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-slate-400 hover:text-white transition-colors no-underline"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="border-t border-white/15">
        <div className="section-container py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-600">
              &copy; {currentYear} Kenluk Pay. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <p className="text-xs text-slate-600 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-fintech-accent inline-block" />
                All systems operational
              </p>
              <p className="text-xs text-slate-600">
                Built for enterprise procurement
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
